/* MINAMI — Rewards bar + checkout nudge popup
   Keeps every bar in sync with the cart and intercepts the Checkout click once
   when a reward is still within reach.
   Tiers and wording: Customise → Settings → Cart drawer.

   EACH TIER HAS ITS OWN TOTAL. A tier whose reward mentions shipping, delivery
   or postage counts the whole cart, sale items included. Every other tier
   counts full-price lines only, which mirrors the live "100 order" 10%
   automatic discount (scoped to the auto-maintained "Full Price" collection).
   Liquid decides which is which and ships the verdict as "fp" on each tier in
   data-tiers.

   Full-price lines are summed at original_line_price, BEFORE the discount
   lands. Using the discounted price would drop the total back under the
   threshold the instant the reward unlocked, and the bar would flicker.

   /cart.js does not return compare_at_price, so the verdict per variant comes
   from two places: the seed Liquid writes into data-fp-seed for whatever is
   already in the cart, and a one-off /products/<handle>.js lookup (cached, and
   deduped per handle) for anything added afterwards. A variant we have not
   managed to classify is counted, so the bar errs towards unlocking early
   rather than withholding a reward the shopper has actually earned.

   No regex literals in this file on purpose — this file is written through an
   API that treats a lone backslash as literal, so a /regex/ literal would
   arrive mangled. */
(function () {
  if (window.__amrbInit) return;
  window.__amrbInit = true;

  var SEEN_KEY = 'amrb_popup_seen';
  var bypass = false;
  var pendingBtn = null;
  var lastFocus = null;

  var originalFetch = window.fetch;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function rawFetch(url, opts) {
    return originalFetch.call(window, url, opts);
  }

  /* ---------- money ---------- */
  function group(digits, sep) {
    var out = '';
    var n = digits.length;
    for (var i = 0; i < n; i++) {
      if (i > 0 && (n - i) % 3 === 0) out += sep;
      out += digits.charAt(i);
    }
    return out;
  }

  function formatMoney(cents, format) {
    var value = cents / 100;

    function num(decimals, thousands, decimalMark) {
      var parts = Math.abs(value).toFixed(decimals).split('.');
      parts[0] = group(parts[0], thousands);
      var body = decimals ? parts.join(decimalMark) : parts[0];
      return (value < 0 ? '-' : '') + body;
    }

    function pick(key) {
      switch (key) {
        case 'amount_no_decimals': return num(0, ',', '.');
        case 'amount_with_comma_separator': return num(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return num(0, '.', ',');
        case 'amount_with_space_separator': return num(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return num(0, ' ', ',');
        case 'amount_with_period_and_space_separator': return num(2, ' ', '.');
        default: return num(2, ',', '.');
      }
    }

    var s = format || '£{{amount}}';
    var out = '';
    var i = 0;
    while (i < s.length) {
      var open = s.indexOf('{{', i);
      if (open === -1) { out += s.slice(i); break; }
      var close = s.indexOf('}}', open);
      if (close === -1) { out += s.slice(i); break; }
      out += s.slice(i, open);
      out += pick(s.slice(open + 2, close).trim());
      i = close + 2;
    }
    return out;
  }

  /* ---------- which lines count ---------- */
  var fpMap = {};      // variant id -> true (full price) / false (on sale)
  var learning = {};   // product handle -> in-flight promise

  function seedFromDom() {
    var bar = document.querySelector('[data-amrb]');
    if (!bar) return;
    var raw = bar.getAttribute('data-fp-seed') || '';
    var parts = raw.split(',');
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      var kv = parts[i].split(':');
      if (kv.length === 2) fpMap[kv[0]] = kv[1] === '1';
    }
  }

  function learn(handle) {
    if (learning[handle]) return learning[handle];
    learning[handle] = rawFetch(root() + 'products/' + handle + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) { return res.json(); })
      .then(function (p) {
        var vs = (p && p.variants) || [];
        for (var i = 0; i < vs.length; i++) {
          var v = vs[i];
          var cap = v.compare_at_price || 0;
          fpMap[v.id] = !(cap > v.price);
        }
      })
      .catch(function () {});
    return learning[handle];
  }

  // Resolves to { gross, qual } in pence.
  function totalsOf(cart) {
    var items = (cart && cart.items) || [];
    var waits = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (fpMap[it.variant_id] === undefined && it.handle) waits.push(learn(it.handle));
    }
    return Promise.all(waits).then(function () {
      var gross = 0;
      var qual = 0;
      for (var j = 0; j < items.length; j++) {
        var line = items[j];
        gross += line.original_line_price;
        if (fpMap[line.variant_id] !== false) qual += line.original_line_price;
      }
      return { gross: gross, qual: qual };
    });
  }

  /* ---------- painting ---------- */
  function tiersOf(el) {
    try { return JSON.parse(el.getAttribute('data-tiers') || '[]'); } catch (e) { return []; }
  }

  function totalForTier(tier, gross, qual) {
    return tier && tier.fp ? qual : gross;
  }

  /* Walks the tiers once and works out everything the bar needs: which reward
     is next, how far off it is, how full the rail should be, and which markers
     are lit. The fill runs to the last unlocked marker and then part way to the
     next one, so two tiers running on two different totals still read as one
     journey. */
  function measure(tiers, gross, qual) {
    var goal = tiers.length ? tiers[tiers.length - 1].t : 0;
    var out = { next: null, gap: 0, pct: 100, done: [] };

    var fill = 0;
    var prevPos = 0;
    var prevTh = 0;
    var locked = false;

    for (var i = 0; i < tiers.length; i++) {
      var th = tiers[i].t;
      var tt = totalForTier(tiers[i], gross, qual);
      var pos = goal > 0 ? (th / goal) * 100 : 0;

      out.done.push(tt >= th);

      if (locked) continue;

      if (tt >= th) {
        fill = pos;
        prevPos = pos;
        prevTh = th;
      } else {
        out.next = tiers[i];
        out.gap = th - tt;

        var span = th - prevTh;
        var into = tt - prevTh;
        if (into < 0) into = 0;
        var frac = span > 0 ? into / span : 0;
        if (frac > 1) frac = 1;
        fill = prevPos + (pos - prevPos) * frac;

        locked = true;
      }
    }

    if (!locked) fill = 100;
    out.pct = Math.max(0, Math.min(100, fill));
    return out;
  }

  function paint(el, gross, qual) {
    var tiers = tiersOf(el);
    if (!tiers.length) return;

    var m = measure(tiers, gross, qual);

    // The bar animates in from 0 on first render. Once it has been painted the
    // width transition takes over, so it slides instead of restarting.
    var fillEl = el.querySelector('.amrb__fill');
    if (fillEl && el.getAttribute('data-painted') === '1') {
      fillEl.style.animation = 'none';
    }
    el.style.setProperty('--amrb-pct', m.pct.toFixed(2) + '%');
    el.setAttribute('data-painted', '1');

    el.setAttribute('data-cart-total', gross);
    el.setAttribute('data-qual-total', qual);
    el.setAttribute('data-gap', m.next ? m.gap : 0);

    var mny = el.getAttribute('data-money-format');
    var doneText = el.getAttribute('data-complete') || '';

    function fillText(tpl) {
      if (!m.next) return doneText;
      return (tpl || '')
        .replace('{amount}', '<strong class="amrb__amount">' + formatMoney(m.gap, mny) + '</strong>')
        .replace('{reward}', m.next.r || '');
    }

    el.classList.toggle('amrb--complete', !m.next);

    var msg = el.querySelector('.amrb__msg');
    if (msg) msg.innerHTML = fillText(el.getAttribute('data-tpl'));

    var title = el.querySelector('.amrb__title');
    if (title) title.innerHTML = fillText(el.getAttribute('data-tpl-title'));

    var nodes = el.querySelectorAll('.amrb__node');
    for (var i = 0; i < nodes.length && i < m.done.length; i++) {
      nodes[i].classList.toggle('is-done', m.done[i]);
    }
  }

  function paintAll(t) {
    var bars = document.querySelectorAll('[data-amrb]');
    for (var i = 0; i < bars.length; i++) paint(bars[i], t.gross, t.qual);
  }

  function refresh() {
    if (!document.querySelector('[data-amrb]')) return Promise.resolve();
    return rawFetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) { return totalsOf(cart); })
      .then(function (t) { paintAll(t); })
      .catch(function () {});
  }

  // The drawer calls this once a variant swap has fully settled.
  window.__amrbRefresh = refresh;

  var CART_WRITES = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  function isCartWrite(url) {
    if (typeof url !== 'string') return false;
    for (var i = 0; i < CART_WRITES.length; i++) {
      if (url.indexOf(CART_WRITES[i]) !== -1) return true;
    }
    return false;
  }

  window.fetch = function () {
    var url = arguments[0];
    if (url && typeof url === 'object' && url.url) url = url.url;
    var request = originalFetch.apply(this, arguments);
    if (isCartWrite(url)) {
      request.then(function () {
        // Mid-swap the cart holds both the old and the new variant, so the
        // total is briefly wrong. The drawer repaints us when it is done.
        if (window.__cdSwapping) return;
        setTimeout(refresh, 80);
      }).catch(function () {});
    }
    return request;
  };

  /* The theme's drawer is a <cart-drawer> element that gets an .open class. */
  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !window.MutationObserver) return;
    new MutationObserver(function () {
      if (drawer.classList.contains('open')) refresh();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- checkout nudge popup ---------- */
  function reveal(pop) { pop.classList.add('is-open'); }

  function openPopup(pop) {
    lastFocus = document.activeElement;
    pop.hidden = false;
    if (window.requestAnimationFrame) requestAnimationFrame(function () { reveal(pop); });
    setTimeout(function () { reveal(pop); }, 30);
    var go = pop.querySelector('[data-amrb-continue]');
    if (go) { try { go.focus(); } catch (e) {} }
    try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (e) {}
  }

  function closePopup(pop) {
    pop.classList.remove('is-open');
    setTimeout(function () { pop.hidden = true; }, 170);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
  }

  var CHECKOUT_SEL = '[name="checkout"], #checkoutCart, .cd__checkout';

  document.addEventListener('click', function (e) {
    if (bypass) return;
    var t = e.target;
    if (!t || !t.closest) return;

    var btn = t.closest(CHECKOUT_SEL);
    if (!btn) return;
    if (btn.closest('.dynamic-checkout__content, .shopify-cleanslate, .amrb-modal')) return;

    var pop = document.getElementById('amrbPopup');
    if (!pop) return;

    if (pop.getAttribute('data-once') === 'true') {
      try { if (sessionStorage.getItem(SEEN_KEY)) return; } catch (err) {}
    }

    var bar = pop.querySelector('[data-amrb]');
    if (!bar) return;

    // data-gap is written by paint() and already knows which total that tier
    // runs on, so the popup never has to work it out again.
    var gap = parseInt(bar.getAttribute('data-gap') || '0', 10);
    if (!gap || gap <= 0) return;

    var maxGap = parseInt(pop.getAttribute('data-max-gap') || '0', 10);
    if (maxGap > 0 && gap > maxGap) return;

    e.preventDefault();
    e.stopPropagation();
    pendingBtn = btn;
    openPopup(pop);
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var pop = t.closest('.amrb-modal');
    if (!pop) return;

    if (t.closest('[data-amrb-continue]')) {
      closePopup(pop);
      bypass = true;
      if (pendingBtn) {
        pendingBtn.click();
      } else {
        window.location.href = root() + 'checkout';
      }
      setTimeout(function () { bypass = false; }, 3000);
    } else if (t.closest('[data-amrb-close]')) {
      closePopup(pop);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var pop = document.getElementById('amrbPopup');
    if (pop && !pop.hidden) closePopup(pop);
  });

  function init() { seedFromDom(); watchDrawer(); refresh(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();